import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { MapPin, Calendar, Weight, User, Truck, Pencil } from 'lucide-react';
import { TransportJob } from '@/hooks/useTransportJobs';
import { useTranslation } from 'react-i18next';

interface MissionCardProps {
  job: TransportJob;
  onAssign: () => void;
}

const getStatusBadge = (job: TransportJob, t: any) => {
  if (job.completed_at) {
    return <Badge className="bg-[#14A38B] text-white">{t('missions.card.status.completed')}</Badge>;
  }
  if (job.started_at) {
    return <Badge className="bg-[#0880AE] text-white">{t('missions.card.status.inProgress')}</Badge>;
  }
  if (job.assigned_at || (job.driver_id && job.vehicle_id)) {
    return <Badge className="bg-[#37699E] text-white">{t('missions.card.status.assigned')}</Badge>;
  }
  return <Badge className="bg-[#F2AC57] text-white">{t('missions.card.status.pending')}</Badge>;
};

export const MissionCard = ({ job, onAssign }: MissionCardProps) => {
  const { t, i18n } = useTranslation();
  const order = job.orders;
  const canAssign = !job.driver_id && !job.vehicle_id && !job.started_at && !job.completed_at;

  const getLocalizedDate = (dateString: string) => {
    const locale = i18n.language === 'en' ? 'en-US' : 
                   i18n.language === 'es' ? 'es-ES' :
                   i18n.language === 'de' ? 'de-DE' :
                   i18n.language === 'it' ? 'it-IT' : 'fr-FR';
    
    return new Date(dateString).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <Card className="rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-[#DBE2EA]">
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="mission-details" className="border-none">
          <AccordionTrigger className="px-6 pt-6 pb-4 hover:no-underline">
            <div className="flex items-start justify-between w-full pr-2">
              <div className="flex-1 text-left">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-medium text-[#37699E] font-heading">
                    {order?.cargo_title || order?.cargo_description}
                  </h3>
                  {getStatusBadge(job, t)}
                </div>
                <p className="text-sm text-[#5A6B7A]">{t('missions.card.order')} {order?.order_number}</p>
              </div>
            </div>
          </AccordionTrigger>

          <AccordionContent>
            <CardContent className="px-6 pb-6 space-y-4">
              {/* Route & Dates */}
              <div className="space-y-2 border-t border-[#DBE2EA] pt-4">
                <div className="flex items-start gap-2">
                  <MapPin size={16} className="text-[#0880AE] flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-[#2D3748] font-medium">
                      {order?.pickup_city}, {order?.pickup_country}
                    </p>
                    <p className="text-xs text-[#5A6B7A]" title={order?.pickup_address}>
                      {order?.pickup_address}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 pl-6">
                  <Calendar size={14} className="text-[#5A6B7A]" />
                  <p className="text-xs text-[#5A6B7A]">
                    {order?.pickup_date && getLocalizedDate(order.pickup_date)}
                  </p>
                </div>

                <div className="flex items-start gap-2 mt-3">
                  <MapPin size={16} className="text-[#14A38B] flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <p className="text-sm text-[#2D3748] font-medium">
                      {order?.delivery_city}, {order?.delivery_country}
                    </p>
                    <p className="text-xs text-[#5A6B7A]" title={order?.delivery_address}>
                      {order?.delivery_address}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 pl-6">
                  <Calendar size={14} className="text-[#5A6B7A]" />
                  <p className="text-xs text-[#5A6B7A]">
                    {order?.delivery_date && getLocalizedDate(order.delivery_date)}
                  </p>
                </div>
              </div>

              {/* Cargo Details */}
              <div className="grid grid-cols-3 gap-4 border-t border-[#DBE2EA] pt-4">
                <div className="flex items-center gap-2">
                  <Weight size={16} className="text-[#5A6B7A]" />
                  <div>
                    <p className="text-xs text-[#5A6B7A]">{t('missions.card.weight')}</p>
                    <p className="text-sm font-medium text-[#2D3748]">{order?.weight_kg} kg</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-[#5A6B7A]">{t('missions.card.type')}</p>
                  <p className="text-sm font-medium text-[#2D3748]">{order?.trailer_type}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5A6B7A]">{t('missions.card.price')}</p>
                  <p className="text-sm font-medium text-[#37699E]">
                    {order?.final_price || order?.proposed_price} €
                  </p>
                </div>
              </div>

              {/* Assignment Info */}
              {(job.driver_id || job.vehicle_id) && (
                <div className="space-y-2 border-t border-[#DBE2EA] pt-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2 flex-1">
                      {job.drivers && (
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-[#0880AE]" />
                          <p className="text-sm text-[#2D3748]">
                            <span className="font-medium">{t('missions.card.driver')}</span>{' '}
                            {job.drivers.users?.first_name} {job.drivers.users?.last_name}
                          </p>
                        </div>
                      )}
                      {job.vehicles && (
                        <div className="flex items-center gap-2">
                          <Truck size={16} className="text-[#0880AE]" />
                          <p className="text-sm text-[#2D3748]">
                            <span className="font-medium">{t('missions.card.vehicle')}</span>{' '}
                            {job.vehicles.plate_number} - {job.vehicles.brand} {job.vehicles.model}
                          </p>
                        </div>
                      )}
                    </div>
                    {!job.started_at && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={onAssign}
                        className="h-8 w-8 p-0 text-[#5A6B7A] hover:text-[#0880AE] hover:bg-[#EBF4F8]"
                      >
                        <Pencil size={16} />
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Actions */}
              {canAssign && (
                <div className="border-t border-[#DBE2EA] pt-4">
                  <Button
                    onClick={onAssign}
                    className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg"
                  >
                    {t('missions.card.assign')}
                  </Button>
                </div>
              )}
            </CardContent>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
};
